'use client'

import ConfirmModal from '@/components/confirm-modal'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import {
    deleteReportAutomation,
    listReportAutomations,
    ReportAutomation,
    ReportAutomationFormat,
    ReportAutomationFrequency,
    ReportAutomationPeriod,
    runReportAutomationNow,
    toggleReportAutomationActive,
} from '@/lib/api/reports/report-automation-api'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdAutorenew,
    MdDelete,
    MdDownload,
    MdEdit,
    MdPlayArrow,
    MdRefresh,
    MdScheduleSend,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import AutomationFormModal from './components/automation-form-modal'

const FREQ_LABEL: Record<ReportAutomationFrequency, string> = {
    DAILY: 'Diário',
    WEEKLY: 'Semanal',
    MONTHLY: 'Mensal',
}

const PERIOD_LABEL: Record<ReportAutomationPeriod, string> = {
    TODAY: 'Hoje',
    YESTERDAY: 'Ontem',
    LAST_7_DAYS: 'Últimos 7 dias',
    LAST_30_DAYS: 'Últimos 30 dias',
    THIS_WEEK: 'Esta semana',
    LAST_WEEK: 'Semana passada',
    THIS_MONTH: 'Este mês',
    LAST_MONTH: 'Mês passado',
    THIS_YEAR: 'Este ano',
}

const FORMAT_LABEL: Record<ReportAutomationFormat, string> = {
    PDF: 'PDF',
    XLSX: 'Excel',
    CSV: 'CSV',
}

const DAY_OF_WEEK_LABELS = [
    'Domingo',
    'Segunda',
    'Terça',
    'Quarta',
    'Quinta',
    'Sexta',
    'Sábado',
]

/** Rótulo do relatório no vocabulário do segmento (Procedimento vira Pedido fora da saúde). */
function buildReportTypeLabels(labels: ServiceLabels): Record<string, string> {
    return {
        consultations: labels.Consultations,
        orders: labels.Orders,
        exams: labels.Exams,
        admissions: 'Internações',
        chats: 'Atendimentos',
        billings: 'Lançamentos',
    }
}

function describeSchedule(a: ReportAutomation): string {
    const hour = `${String(a.hourOfDay).padStart(2, '0')}:00`
    if (a.frequency === 'DAILY') return `Todo dia às ${hour}`
    if (a.frequency === 'WEEKLY' && a.dayOfWeek !== null) {
        return `Toda ${DAY_OF_WEEK_LABELS[a.dayOfWeek].toLowerCase()} às ${hour}`
    }
    if (a.frequency === 'MONTHLY' && a.dayOfMonth !== null) {
        return `Todo dia ${a.dayOfMonth} às ${hour}`
    }
    return FREQ_LABEL[a.frequency]
}

function formatDateTime(iso: string | null): string {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
    })
}

export default function ReportAutomationsPage() {
    const navigate = useNavigate()
    const reportTypeLabels = buildReportTypeLabels(useServiceLabel())
    const [items, setItems] = useState<ReportAutomation[]>([])
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState<ReportAutomation | null>(null)
    const [isCreating, setIsCreating] = useState(false)
    const [toDelete, setToDelete] = useState<ReportAutomation | null>(null)
    const [processing, setProcessing] = useState(false)

    const load = async () => {
        setLoading(true)
        try {
            const res = await listReportAutomations()
            if (res.success && res.result) setItems(res.result)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const handleToggle = async (a: ReportAutomation) => {
        const res = await toggleReportAutomationActive(a.id)
        if (res.success) {
            toast.success(a.active ? 'Automação pausada' : 'Automação ativada')
            load()
        } else {
            toast.error(res.error || 'Erro ao alternar')
        }
    }

    const handleRunNow = async (a: ReportAutomation) => {
        const tid = toast.loading('Executando automação...')
        try {
            const res = await runReportAutomationNow(a.id)
            toast.dismiss(tid)
            if (res.success) {
                toast.success('Relatório gerado e enviado por email')
                load()
            } else {
                toast.error(res.error || 'Erro ao executar')
            }
        } catch (err: any) {
            toast.dismiss(tid)
            toast.error(err?.message || 'Erro ao executar')
        }
    }

    const handleDelete = async () => {
        if (!toDelete) return
        setProcessing(true)
        try {
            const res = await deleteReportAutomation(toDelete.id)
            if (res.success) {
                toast.success('Automação removida')
                setToDelete(null)
                load()
            } else {
                toast.error(res.error || 'Erro ao remover')
            }
        } finally {
            setProcessing(false)
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => navigate('/core/reports')}
                >
                    <MdArrowBack /> Voltar
                </button>
                <div className="flex items-center gap-3 flex-1">
                    <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center">
                        <MdScheduleSend className="text-2xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Automações de Relatórios</h1>
                        <p className="text-sm text-base-content/60">
                            Configure relatórios para serem gerados automaticamente e enviados por email
                        </p>
                    </div>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={load}>
                    <MdRefresh /> Atualizar
                </button>
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                        setEditing(null)
                        setIsCreating(true)
                    }}
                >
                    <MdAdd /> Nova automação
                </button>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    {loading && (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <span className="loading loading-spinner loading-lg text-secondary" />
                        </div>
                    )}

                    {!loading && items.length === 0 && (
                        <div className="text-center py-16 text-base-content/60">
                            <MdAutorenew className="text-5xl mx-auto mb-2 opacity-40" />
                            <p className="font-medium">Nenhuma automação configurada</p>
                            <p className="text-sm mt-1">
                                Crie uma nova automação para receber relatórios automaticamente por email
                            </p>
                        </div>
                    )}

                    {!loading && items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Nome</th>
                                        <th>Relatório</th>
                                        <th>Período</th>
                                        <th>Agenda</th>
                                        <th>Próxima execução</th>
                                        <th>Última execução</th>
                                        <th>Destinatários</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((a) => (
                                        <tr key={a.id} className="hover">
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="toggle toggle-success toggle-sm"
                                                    checked={a.active}
                                                    onChange={() => handleToggle(a)}
                                                    title={a.active ? 'Pausar' : 'Ativar'}
                                                />
                                            </td>
                                            <td className="font-medium">
                                                {a.name}
                                                <div className="text-xs text-base-content/50">
                                                    {FORMAT_LABEL[a.format]}
                                                </div>
                                            </td>
                                            <td className="text-sm">
                                                {reportTypeLabels[a.reportType] ||
                                                    a.reportType}
                                            </td>
                                            <td className="text-sm">
                                                {PERIOD_LABEL[a.periodWindow]}
                                            </td>
                                            <td className="text-sm">
                                                {describeSchedule(a)}
                                            </td>
                                            <td className="text-sm whitespace-nowrap">
                                                {formatDateTime(a.nextRunAt)}
                                            </td>
                                            <td className="text-sm whitespace-nowrap">
                                                {a.lastRunAt ? (
                                                    <div>
                                                        <span>{formatDateTime(a.lastRunAt)}</span>
                                                        {a.lastSuccess === false && (
                                                            <div
                                                                className="text-xs text-error truncate max-w-[200px]"
                                                                title={a.lastError || ''}
                                                            >
                                                                Falha: {a.lastError}
                                                            </div>
                                                        )}
                                                        {a.lastSuccess === true && (
                                                            <div className="text-xs text-success">
                                                                Enviado
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="text-xs">
                                                {a.recipients.length === 0
                                                    ? '—'
                                                    : a.recipients.length === 1
                                                    ? a.recipients[0]
                                                    : `${a.recipients[0]} +${a.recipients.length - 1}`}
                                            </td>
                                            <td className="whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() => handleRunNow(a)}
                                                        title="Executar agora"
                                                    >
                                                        <MdPlayArrow className="text-lg" />
                                                    </button>
                                                    {a.lastFileUrl && (
                                                        <a
                                                            className="btn btn-ghost btn-xs text-secondary"
                                                            href={a.lastFileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="Baixar último relatório gerado"
                                                        >
                                                            <MdDownload className="text-lg" />
                                                        </a>
                                                    )}
                                                    <button
                                                        className="btn btn-ghost btn-xs"
                                                        onClick={() => {
                                                            setEditing(a)
                                                            setIsCreating(false)
                                                        }}
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-lg" />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={() => setToDelete(a)}
                                                        title="Remover"
                                                    >
                                                        <MdDelete className="text-lg" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {(editing || isCreating) && (
                <AutomationFormModal
                    initial={editing}
                    onClose={() => {
                        setEditing(null)
                        setIsCreating(false)
                    }}
                    onSaved={() => {
                        setEditing(null)
                        setIsCreating(false)
                        load()
                    }}
                />
            )}

            {toDelete && (
                <ConfirmModal
                    isOpen={!!toDelete}
                    onClose={() => setToDelete(null)}
                    onConfirm={handleDelete}
                    title="Remover automação"
                    message={`Tem certeza que deseja remover "${toDelete.name}"?`}
                    loading={processing}
                    confirmButtonText="Remover"
                    confirmButtonClass="btn-error"
                />
            )}
        </div>
    )
}
